import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import PlantApi from "../../services/PlantApi";
import CompanyAPI from "../../services/CompanyApi";
import { useToast } from "../../context/ToastContext";

// Design System
import FormCard from "../../components/FormCard";
import Input from "../../components/Input/Input";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";

const CreatePlantForm: React.FC = () => {
  const navigate = useNavigate();
  const { displayToast } = useToast();
  const { t: translateForm } = useTranslation("form");
  const { t: translateMessages } = useTranslation("messages");
  const { t: translateGeneral } = useTranslation("general");

  const [name, setName] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [companies, setCompanies] = useState<{ label: string; value: string }[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async (): Promise<void> => {
    try {
      const response = await CompanyAPI.GetAllCompanies();
      const options = (response.data || []).map((c: any) => ({
        label: c.name,
        value: c.id,
      }));
      setCompanies(options.sort((a: any, b: any) => a.label.localeCompare(b.label)));
    } catch (error) {
      console.error("Erro ao buscar empresas: ", error);
    }
  };

  const handleSave = async (): Promise<void> => {
    if (!name || !companyId) {
      displayToast("error", translateForm("error"), translateMessages("fillAllFields"), false, 3000);
      return;
    }

    setIsLoading(true);
    try {
      // Check for duplicates (local check as per legacy, since API doesn't have a check endpoint)
      const plantsResponse = await PlantApi.GetAllPlants();
      const plants = plantsResponse.data || [];
      const isDuplicate = plants.some(
        (p: any) => p.name.toLowerCase() === name.toLowerCase() && p.companyId === companyId
      );

      if (isDuplicate) {
        displayToast("error", translateMessages("duplicatePlantError"), "", false, 3000);
        return;
      }

      await PlantApi.CreatePlant(companyId, name);
      displayToast("success", translateMessages("registerSuccess"), "", false, 3000);
      navigate("/plants");
    } catch (error: any) {
      displayToast("error", translateForm("error"), error.message || error, false, 3000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormCard
      title={translateMessages("newPlant")}
      icon="add"
      onSubmit={handleSave}
      onCancel={() => setShowConfirmCancel(true)}
      submitButtonLabel={translateGeneral("save")}
      cancelButtonLabel={translateGeneral("cancel")}
      disabled={isLoading}
      numColumns={6}
    >
      <div style={{ gridColumn: "span 3" }}>
        <Input
          type="text"
          label={translateForm("name")}
          required
          value={name}
          onChange={(val: any) => setName(val)}
        />
      </div>
      <div style={{ gridColumn: "span 3" }}>
        <Input
          type="select"
          label={translateForm("company")}
          required
          value={companyId}
          options={companies}
          optionLabel="label"
          optionValue="value"
          onChange={(val: any) => setCompanyId(val)}
        />
      </div>

      <ConfirmModal
        visible={showConfirmCancel}
        onHide={() => setShowConfirmCancel(false)}
        onConfirm={() => navigate("/plants")}
      />
    </FormCard>
  );
};

export default CreatePlantForm;
